'use client';
import { gsap } from 'gsap';
import { memo, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { VStack } from '@chakra-ui/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';

import { breakpoints } from 'app/themeConfig';
import { getOffsetValues } from './utils';

gsap.registerPlugin(useGSAP, ScrollTrigger);

ScrollTrigger.config({
  ignoreMobileResize: true,
});

const defaultCardsGaps = { mobile: 50, tablet: 65, laptop: 90, desktop: 90 };

// TODO: Начать использовать этот компонент во всех местах где необходимо использовать логику аккордеона

/**
 * Компонент-обёртка для реализации аккордеона с использованием GSAP и ScrollTrigger.
 *
 * - Каждая дочерняя карточка фиксируется (pin) при скролле.
 * - Анимация работает на всех брейкпоинтах, логика адаптируется через gsap.matchMedia.
 * - Отступы между карточками можно настроить через пропс cardsGaps.
 *
 * @param {Object} props - Свойства компонента.
 * @param {React.ReactNode} props.children - Дочерние элементы (карточки аккордеона).
 * @param {Object} [props.cardsGaps=defaultCardsGaps] - Объект с отступами между карточками.
 * @param {number} props.cardsGaps.mobile - Отступ для мобильных устройств.
 * @param {number} props.cardsGaps.tablet - Отступ для планшетов.
 * @param {number} props.cardsGaps.laptop - Отступ для ноутбуков.
 * @param {number} props.cardsGaps.desktop - Отступ для десктопов.
 *
 * @example
 * <AccordionLogic>
 *   {cardsData.map((cardData, i) => (
        <CardComponent key={`accordion-card-${i}`} {...cardData} />
      ))}
 * </AccordionLogic>
 */
const AccordionLogic = ({ children, cardsGaps = defaultCardsGaps, ...props }) => {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      const gsapInit = context => {
        const conditions = context.conditions;
        const cards = gsap.utils.toArray(containerRef.current.children);

        cards.forEach((card, i) => {
          ScrollTrigger.create({
            trigger: card,
            pin: true,
            scrub: 1,
            // @ts-ignore
            endTrigger: cards.at(-1),
            start: () => `top ${getOffsetValues(i + 1, conditions, cardsGaps)}px`,
            end: () => `top ${getOffsetValues(cards.length, conditions, cardsGaps)}px`,
            pinSpacing: false,
          });
        });
      };
      mm.add(
        {
          isDesktop: `(min-width: ${breakpoints.desktop})`,
          isLaptop: `(min-width: ${breakpoints.laptop}) and (max-width: calc(${breakpoints.desktop} - 1px))`,
          isTablet: `(min-width: ${breakpoints.tablet}) and (max-width: calc(${breakpoints.laptop} - 1px))`,
          isMg: `(min-width: ${breakpoints.mg})`,
          isMobile: `(max-width: calc(${breakpoints.tablet} - 1px)`,
        },
        context => gsapInit(context),
      );
    },
    { scope: containerRef.current },
  );

  return (
    <VStack
      ref={containerRef}
      w="full"
      gap={{ mobile: '20px', tablet: '16px', laptop: '20px' }}
      {...props}
    >
      {children}
    </VStack>
  );
};

export default memo(AccordionLogic);
